// src/pages/auth/SignInPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { useLoginMutation } from "../../app/api/authApi";

type LocationState = { from?: { pathname?: string } };

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getErrorMessage = (err: any): string => {
  // RTK Query typical shapes: { data: { message } } or { error: string }
  const msg =
    err?.data?.message ||
    err?.data?.error ||
    err?.error ||
    err?.message ||
    "Something went wrong. Please try again.";
  return typeof msg === "string"
    ? msg
    : "Something went wrong. Please try again.";
};

const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;

  const [login, { isLoading, isSuccess, error }] = useLoginMutation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [touched, setTouched] = useState({ email: false, password: false });

  const from = state.from?.pathname || "/user/dashboard";

  const emailError = useMemo(() => {
    if (!touched.email) return "";
    if (!email.trim()) return "Email is required.";
    if (!emailRegex.test(email.trim())) return "Enter a valid email address.";
    return "";
  }, [email, touched.email]);

  const passwordError = useMemo(() => {
    if (!touched.password) return "";
    if (!password) return "Password is required.";
    if (password.length < 6) return "Password must be at least 6 characters.";
    return "";
  }, [password, touched.password]);

  const formHasError = Boolean(emailError || passwordError);

  useEffect(() => {
    if (isSuccess) navigate(from, { replace: true });
  }, [isSuccess, navigate, from]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!email.trim() || !emailRegex.test(email.trim()) || password.length < 6)
      return;

    await login({
      email: email.trim().toLowerCase(),
      password,
      // If your backend supports it, keep; otherwise harmless extra field (remove if strict)
      rememberMe,
    } as any);
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-center px-4 py-12">
        <div className="grid w-full grid-cols-1 gap-10 md:grid-cols-2 md:items-center">
          {/* Left: Brand / Message */}
          <div className="hidden md:block">
            <div className="max-w-md">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-sm text-slate-600 shadow-sm backdrop-blur">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                Secure access
              </div>

              <h1 className="mt-5 text-3xl font-bold tracking-tight text-slate-900">
                Welcome back.
              </h1>
              <p className="mt-3 text-slate-600">
                Sign in to continue building, launching, and scaling your offers
                with your system — all in one place.
              </p>

              <div className="mt-7 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">Tip</p>
                <p className="mt-2 text-sm text-slate-600">
                  If you’re signing in on a shared device, uncheck “Remember
                  me”.
                </p>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="mx-auto w-full max-w-md">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-6 py-5">
                <h2 className="text-xl font-semibold text-slate-900">
                  Sign in
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Enter your details to access your dashboard.
                </p>
              </div>

              <form onSubmit={onSubmit} className="px-6 py-6">
                {error && (
                  <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {getErrorMessage(error)}
                  </div>
                )}

                <label className="block text-sm font-medium text-slate-700">
                  Email address
                </label>
                <div
                  className={[
                    "mt-2 flex items-center gap-2 rounded-xl border bg-white px-3 py-2.5 shadow-sm transition",
                    emailError
                      ? "border-red-300 focus-within:ring-2 focus-within:ring-red-200"
                      : "border-slate-200 focus-within:border-slate-300 focus-within:ring-2 focus-within:ring-slate-200",
                  ].join(" ")}
                >
                  <Mail className="h-4 w-4 text-slate-400" />
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                  />
                </div>
                {emailError && (
                  <p className="mt-2 text-xs text-red-600">{emailError}</p>
                )}

                <div className="mt-5 flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-700">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-slate-700 underline underline-offset-4 hover:text-slate-900"
                  >
                    Forgot?
                  </Link>
                </div>

                <div
                  className={[
                    "mt-2 flex items-center gap-2 rounded-xl border bg-white px-3 py-2.5 shadow-sm transition",
                    passwordError
                      ? "border-red-300 focus-within:ring-2 focus-within:ring-red-200"
                      : "border-slate-200 focus-within:border-slate-300 focus-within:ring-2 focus-within:ring-slate-200",
                  ].join(" ")}
                >
                  <Lock className="h-4 w-4 text-slate-400" />
                  <input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="rounded-lg p-1 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-2 text-xs text-red-600">{passwordError}</p>
                )}

                <div className="mt-5 flex items-center justify-between">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-200"
                    />
                    Remember me
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || formHasError}
                  className={[
                    "mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition",
                    isLoading || formHasError
                      ? "cursor-not-allowed bg-slate-200 text-slate-500"
                      : "bg-slate-900 text-white hover:bg-slate-800",
                  ].join(" ")}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    "Sign in"
                  )}
                </button>

                <p className="mt-5 text-center text-sm text-slate-600">
                  Don’t have an account?{" "}
                  <Link
                    to="/register"
                    className="font-semibold text-slate-900 underline underline-offset-4 hover:text-slate-700"
                  >
                    Create one
                  </Link>
                </p>
              </form>
            </div>

            {/* Small footer */}
            <p className="mt-5 text-center text-xs text-slate-500">
              By continuing, you agree to our Terms & Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
